import { useMediaQuery } from "@/hooks";
import { formatCurrency } from "@/lib/currency/currency";
import { Spacer } from "@/offering/spacer";
import { colors } from "@/offering_v2/components/colors";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import classNames from "classnames";
import {
  MembershipCharge,
  useMembershipChargesQuery,
} from "../hooks/use_membership_charges_query";
import { PortalPathParams } from "../hooks/use_portal_history";
import { PortalBillBreakdownModal } from "./portal_bill_breakdown_modal";
import { PortalButton } from "./portal_button";
import styles from "./portal_membership_charges_table.module.css";
import { isNumber } from "lodash";
import { useMembershipManagementAnalytics } from "../hooks/use_membership_management_analytics";

interface PortalMembershipChargesTableProps {
  partnerId: string;
}

export function PortalMembershipChargesTable({
  partnerId,
}: PortalMembershipChargesTableProps) {
  const { device } = useMediaQuery();
  const { subscriptionId } = useParams<PortalPathParams>();

  const analyticEvent = useMembershipManagementAnalytics();
  const { data: chargesData } = useMembershipChargesQuery(
    partnerId,
    subscriptionId
  );

  const [viewAllCharges, setViewAllCharges] = useState(false);
  const [viewingChargeDetails, setViewingChargeDetails] =
    useState<MembershipCharge>();

  const columnClasses =
    device === "mobile"
      ? {
          date: "col-5",
          membershipPlan: "d-none",
          paymentMethod: "d-none",
          total: "col-4",
          details: "col-3 text-end",
        }
      : {
          date: "col-2",
          membershipPlan: "col-5",
          paymentMethod: "col-3",
          total: "col-1",
          details: "col-1 text-end",
        };

  const handleClickChargeDetails = (charge: MembershipCharge) => {
    setViewingChargeDetails(charge);
    analyticEvent("Charge Details Click");
  };

  const mapChargeRow = (charge: MembershipCharge) => {
    return (
      <div key={charge.id} className="row  py-3 border-bottom">
        <div className={columnClasses.date}>{charge.paymentDate}</div>
        <div
          className={classNames(
            columnClasses.membershipPlan,
            styles.membershipPlan
          )}
        >
          {charge.membershipPlan.name}: {charge.membershipPlan.location}
        </div>
        <div className={columnClasses.paymentMethod}>
          {charge.promotionDescription && charge.totalPrice === 0 ? (
            <div style={{ color: colors.spinachCore }}>
              {charge.promotionDescription}
            </div>
          ) : (
            <div>
              <img
                className="me-3"
                src={charge.cardImageUrl}
                alt={charge.cardBrand}
                width={33}
                height={22}
              />
              Ending with {charge.cardEnding}
            </div>
          )}
        </div>
        <div className={columnClasses.total}>
          {charge.transactionType === "charge" ? (
            formatCurrency(charge.totalPrice, charge.currency)
          ) : charge.transactionType === "refund" &&
            isNumber(charge.refundedAmount) ? (
            <div className="text-nowrap">
              {formatCurrency(
                -Math.abs(charge.refundedAmount),
                charge.currency
              )}
              <br />
              (Refunded)
            </div>
          ) : null}
        </div>
        <div className={columnClasses.details}>
          <PortalButton
            appearance="text"
            onClick={() => handleClickChargeDetails(charge)}
          >
            Details
          </PortalButton>
        </div>
      </div>
    );
  };

  const numberOfDefaultDisplayedCharges = 2;
  const hasMoreThanDefaultCharges = !!(
    chargesData && chargesData?.length > numberOfDefaultDisplayedCharges
  );
  const defaultDisplayedCharges = chargesData?.slice(
    0,
    numberOfDefaultDisplayedCharges
  );
  const displayedCharges = viewAllCharges
    ? chargesData
    : defaultDisplayedCharges;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <div className="fw-bold fs-3">Charges</div>
        {hasMoreThanDefaultCharges && (
          <PortalButton
            appearance="text"
            onClick={() => setViewAllCharges((v) => !v)}
          >
            {viewAllCharges ? "See less" : "View all"}
          </PortalButton>
        )}
      </div>
      <Spacer size={24} />
      <div>
        <div className="row py-3 border-bottom">
          <b className={columnClasses.date}>Date</b>
          <b className={columnClasses.membershipPlan}>Membership plan</b>
          <b className={columnClasses.paymentMethod}>Payment method</b>
          <b className={columnClasses.total}>Total</b>
        </div>
        {displayedCharges?.map(mapChargeRow)}
      </div>

      {viewingChargeDetails && (
        <PortalBillBreakdownModal
          show={!!viewingChargeDetails}
          onHide={() => setViewingChargeDetails(undefined)}
          totalAmount={viewingChargeDetails.totalPrice}
          currency={viewingChargeDetails.currency}
          monthlyPaymentAmount={viewingChargeDetails.unitPrice}
          taxAmount={viewingChargeDetails.taxAmount}
          taxDescription={viewingChargeDetails.taxDescription}
          promotionAmount={viewingChargeDetails.promotionAmount}
          promotionCode={viewingChargeDetails.promotionCode}
          isRefunded={viewingChargeDetails.transactionType === "refund"}
          paymentCycles={
            viewingChargeDetails.isFirstCharge &&
            viewingChargeDetails.adjustmentAmount !== 0
              ? viewingChargeDetails.paymentCycles
              : viewingChargeDetails.paymentCycles - 1
          }
          adjustmentAmount={viewingChargeDetails.adjustmentAmount}
          refundedAmount={viewingChargeDetails.refundedAmount}
          feeLines={viewingChargeDetails.feeLines}
        />
      )}
    </div>
  );
}
